import React from 'react';
import { FieldErrors } from 'react-hook-form';
import { LuCircleMinus, LuPlus, LuX } from 'react-icons/lu';

import { AssetAllocation } from '@ndvr/portfolio-api';

import { AssetClass, AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { TickerSelectorInput } from '@/components/TickerSelectorInput';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { WEIGHT_INPUT_REGEX } from '@/components/ui/WeightInput';

import { calculateTotalWeight } from '../shared-utils';

// This is the security's own instrument type (a bond ETF is still structurally "Equity" — it trades as
// a share). It is NOT look-through economic exposure; that's what the exposure panel is for.
const TYPE_STYLES: Record<AssetClass, { bg: string; fg: string }> = {
    [AssetClass.EQUITY]: { bg: 'bg-green-50', fg: 'text-green-700' },
    [AssetClass.BOND]: { bg: 'bg-gold-0', fg: 'text-gold-600' },
    [AssetClass.OPTIONS]: { bg: 'bg-purple-50', fg: 'text-purple-700' },
    [AssetClass.FUTURES]: { bg: 'bg-orange-50', fg: 'text-orange-700' },
    [AssetClass.OTHER]: { bg: 'bg-slate-100', fg: 'text-slate-700' },
};

interface StrategyAssetTableProps {
    assetAllocations: AssetAllocation[];
    getAssetInfo: (securityId: string) => AssetInfo | undefined;
    errors?: FieldErrors<{ assetAllocations: AssetAllocation[] }>;
    onUpdateWeight: (securityId: string, weight: string) => void;
    onRemoveAsset: (securityId: string) => void;
    onAddReplacement: (asset: AssetInfo) => void;
    onRemoveReplacement: (assetSecurityId: string, replacementSecurityId: string) => void;
    addingReplacementForAsset: string | null;
    setAddingReplacementForAsset: (securityId: string | null) => void;
    showAddAsset: boolean;
    onAddAsset: (asset: AssetInfo) => void;
    onShowAddAsset: () => void;
    onCancelAdd: () => void;
    onEvenSplit: () => void;
    readOnly?: boolean;
}

const EDITABLE_GRID_COLUMNS = 'grid-cols-[80px_minmax(140px,1fr)_70px_100px_minmax(180px,1.2fr)_36px]';
const READ_ONLY_GRID_COLUMNS = 'grid-cols-[80px_minmax(140px,1fr)_70px_100px_minmax(180px,1.2fr)]';

export const StrategyAssetTable: React.FC<StrategyAssetTableProps> = ({
    assetAllocations,
    getAssetInfo,
    errors,
    onUpdateWeight,
    onRemoveAsset,
    onAddReplacement,
    onRemoveReplacement,
    addingReplacementForAsset,
    setAddingReplacementForAsset,
    showAddAsset,
    onAddAsset,
    onShowAddAsset,
    onCancelAdd,
    onEvenSplit,
    readOnly = false,
}) => {
    const displayWeight = calculateTotalWeight(assetAllocations);
    const gridColumns = readOnly ? READ_ONLY_GRID_COLUMNS : EDITABLE_GRID_COLUMNS;
    const isValidTotal = displayWeight === 100;

    const handleWeightChange = (securityId: string, rawValue: string) => {
        if (rawValue === '' || WEIGHT_INPUT_REGEX.test(rawValue)) {
            const normalized = rawValue.replace(',', '.');
            const numValue = parseFloat(normalized);
            if (normalized === '' || (numValue >= 0 && numValue <= 100)) {
                onUpdateWeight(securityId, normalized);
            }
        }
    };

    return (
        <div className="border-cocoa-200 overflow-x-auto rounded-lg border bg-white">
            <div className={`grid ${gridColumns} border-cocoa-200 bg-cocoa-0 min-w-[660px] gap-2.5 border-b px-5 py-3`}>
                <span className="text-cocoa-500 text-xs font-semibold tracking-wide uppercase">Ticker</span>
                <span className="text-cocoa-500 text-xs font-semibold tracking-wide uppercase">Name</span>
                <span className="text-cocoa-500 text-xs font-semibold tracking-wide uppercase">Type</span>
                <span className="text-cocoa-500 text-right text-xs font-semibold tracking-wide uppercase">Weight</span>
                <span className="text-cocoa-500 text-xs font-semibold tracking-wide uppercase">TLH replacement</span>
                {!readOnly && <span />}
            </div>

            {errors?.assetAllocations && (
                <p className="px-5 py-2 text-sm text-red-600">{errors.assetAllocations.message}</p>
            )}

            {assetAllocations.map((asset, index) => {
                const assetInfo = getAssetInfo(asset.securityId);
                const typeStyle = assetInfo?.assetClass ? TYPE_STYLES[assetInfo.assetClass] : undefined;
                const weightError = errors?.assetAllocations?.[index]?.weight;

                return (
                    <div
                        key={asset.securityId}
                        className={`grid ${gridColumns} border-cocoa-200/50 min-w-[660px] items-center gap-2.5 border-t px-5 py-3`}
                    >
                        <span className="text-cocoa-1000 text-sm font-semibold">
                            {assetInfo?.symbol || asset.securityId}
                        </span>
                        <span className="text-cocoa-700 truncate text-sm">{assetInfo?.description || 'Unknown'}</span>
                        <span>
                            {typeStyle && assetInfo?.assetClass && (
                                <span
                                    className={`inline-flex items-center rounded px-1.5 py-1 text-[11px] font-semibold tracking-wide uppercase ${typeStyle.bg} ${typeStyle.fg}`}
                                >
                                    {assetInfo.assetClass}
                                </span>
                            )}
                        </span>
                        <div className="flex flex-col items-end gap-1">
                            <div
                                className={`flex h-8 w-[74px] items-center justify-end gap-0.5 rounded-md border px-2 ${
                                    weightError ? 'border-red-500' : 'border-cocoa-200'
                                }`}
                            >
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={asset.weight}
                                    onChange={(e) => handleWeightChange(asset.securityId, e.target.value)}
                                    readOnly={readOnly}
                                    className="text-cocoa-1000 w-8 border-0 bg-transparent text-right text-sm font-medium outline-none"
                                />
                                <span className="text-cocoa-500 text-sm">%</span>
                            </div>
                            {weightError && <span className="text-xs text-red-600">{weightError.message}</span>}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                            {asset.replacementAssets.map((replacement) => {
                                const replacementInfo = getAssetInfo(replacement.securityId);
                                return (
                                    <span
                                        key={replacement.securityId}
                                        className={`bg-seafoam-0 text-seafoam-600 inline-flex items-center gap-1 rounded-full py-1 text-xs font-medium ${
                                            readOnly ? 'px-2.5' : 'pr-1.5 pl-2.5'
                                        }`}
                                    >
                                        {replacementInfo?.symbol || replacement.securityId}
                                        {!readOnly && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onRemoveReplacement(asset.securityId, replacement.securityId)
                                                }
                                                className="opacity-70 hover:opacity-100"
                                            >
                                                <LuX className="h-3 w-3" />
                                            </button>
                                        )}
                                    </span>
                                );
                            })}
                            {!readOnly && (
                                <Popover
                                    open={addingReplacementForAsset === asset.securityId}
                                    onOpenChange={(open) =>
                                        setAddingReplacementForAsset(open ? asset.securityId : null)
                                    }
                                >
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            className="border-cocoa-300 text-cocoa-400 hover:border-cocoa-900 hover:text-cocoa-900 flex h-6 w-6 items-center justify-center rounded-full border border-dashed"
                                        >
                                            <LuPlus className="h-3.5 w-3.5" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-80" align="start">
                                        <TickerSelectorInput
                                            onSelectTicker={onAddReplacement}
                                            placeholder="Search for replacement..."
                                            label="Select replacement asset"
                                            excludeItems={[
                                                {
                                                    sId: asset.securityId,
                                                    scId: asset.shareClassId,
                                                    displayText: 'Original asset',
                                                },
                                                ...asset.replacementAssets.map((r) => ({
                                                    sId: r.securityId,
                                                    scId: r.shareClassId,
                                                    displayText: 'Already added',
                                                })),
                                            ]}
                                            autoFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>
                        {!readOnly && (
                            <button
                                type="button"
                                onClick={() => onRemoveAsset(asset.securityId)}
                                className="text-cocoa-400 hover:bg-cocoa-0 flex h-8 w-8 items-center justify-center rounded-md hover:text-red-600"
                            >
                                <LuCircleMinus className="h-4.5 w-4.5" />
                            </button>
                        )}
                    </div>
                );
            })}

            <div className="border-cocoa-200 bg-cocoa-0 flex flex-wrap items-center gap-3 border-t px-5 py-3">
                {!readOnly &&
                    (showAddAsset ? (
                        <div className="flex w-full items-center gap-2">
                            <div className="min-w-[280px] flex-1">
                                <TickerSelectorInput
                                    onSelectTicker={onAddAsset}
                                    placeholder="Search for a ticker..."
                                    label="Select asset"
                                    excludeItems={assetAllocations.map((a) => ({
                                        sId: a.securityId,
                                        scId: a.shareClassId,
                                        displayText: 'Already added',
                                    }))}
                                    autoFocus
                                    usePortal
                                />
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-[13px] font-medium"
                                onClick={onCancelAdd}
                            >
                                Cancel
                            </Button>
                        </div>
                    ) : (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1.5 text-[13px] font-medium"
                            onClick={onShowAddAsset}
                        >
                            <LuPlus className="h-4 w-4" /> Add a ticker
                        </Button>
                    ))}
                <span className={`ml-auto text-sm font-medium ${isValidTotal ? 'text-cocoa-1000' : 'text-red-600'}`}>
                    Total weight: {displayWeight}%
                </span>
                {!readOnly && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-[13px] font-medium"
                        onClick={onEvenSplit}
                    >
                        Even split
                    </Button>
                )}
            </div>
        </div>
    );
};
